import { Box, InputAdornment, MenuItem, Select, TextField } from '@mui/material'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputHeader } from '../Headers/styles'
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk'
import { NumberFormatCustom, NumberFormatCustomProps } from './NumberFormatCustom'

interface CombinedSelectProps {
  nameValue: string
  nameUnit: string
  labelValue: string | number
  labelUnitValue?: string
  optionsUnit: { label: string; value: string | number }[]
  type?: 'text' | 'number'
  showLabel?: boolean
  numberFormatProps?: NumberFormatCustomProps
  disabled?: boolean
  isUnitDisabled?: boolean
  required?: boolean
  maxLength?: number
  maxValue?: number
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const CombinedSelect: React.FC<CombinedSelectProps> = ({
  nameValue,
  nameUnit,
  labelValue,
  optionsUnit,
  numberFormatProps,
  labelUnitValue,
  type = 'number',
  showLabel = false,
  disabled = false,
  isUnitDisabled = false,
  required = false,
  maxLength,
  maxValue,
  onChange,
}) => {
  const { control, trigger } = useFormContext()

  return (
    <Box mb={2} sx={{ backgroundColor: 'transparent' }}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <InputHeader gutterBottom>
          <Box component='span' display='inline-flex' alignItems='baseline'>
            {labelValue}
            <Box component='span' visibility={required ? 'visible' : 'hidden'} ml={0.5}>
              <RequiredAsterisk />
            </Box>
          </Box>
        </InputHeader>
        {labelUnitValue && (
          <InputHeader gutterBottom sx={{ marginBottom: '-4px' }}>
            <Box component='span' display='inline-flex' alignItems='baseline'>
              {labelUnitValue}
            </Box>
          </InputHeader>
        )}
      </Box>
      <Controller
        name={nameValue}
        control={control}
        render={({
          field: { onChange: formOnChange, value, name, ref },
          fieldState: { error },
        }) => (
          <TextField
            label={showLabel ? labelValue : ''}
            variant='outlined'
            fullWidth
            value={value}
            onChange={(e) => {
              formOnChange(e)
              if (onChange) {
                onChange(e)
              }
            }}
            error={!!error}
            disabled={disabled}
            helperText={error?.message}
            sx={{
              '& .MuiInputBase-root': { paddingRight: '4px' },
              '& .MuiOutlinedInput-input': { paddingRight: 0 },
              '& .MuiInputBase-input': { paddingRight: '4px' },
              '& .MuiFormHelperText-root': { backgroundColor: 'transparent' },
            }}
            InputProps={{
              inputComponent: type === 'number' ? NumberFormatCustom : undefined,
              inputProps: {
                ...numberFormatProps,
                name: name,
                onChange: onChange,
                ref: ref,
                maxLength: maxLength,
                maxValue: maxValue,
                sx: {
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  backgroundColor: '#FFFFFF',
                },
              },
              endAdornment: (
                <InputAdornment
                  position='end'
                  sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    border: 'none',
                    m: 0,
                    minHeight: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#FFFFFF',
                      p: 0,
                      m: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'stretch',
                    }}
                  >
                    <Controller
                      name={nameUnit}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            trigger(nameValue)
                          }}
                          variant='standard'
                          disableUnderline
                          disabled={disabled || isUnitDisabled}
                          sx={{
                            backgroundColor: '#FFFFFF',
                            boxShadow: 'none',
                            border: 'none',
                            p: 0,
                            m: 0,
                            minHeight: 'unset !important',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'stretch',
                            '&:hover': { backgroundColor: '#FFFFFF' },
                            '&.Mui-focused': { backgroundColor: '#FFFFFF' },
                            '&.Mui-selected': { backgroundColor: '#FFFFFF' },
                            '& .MuiSelect-select': {
                              backgroundColor: '#FFFFFF !important',
                              paddingRight: '4px',
                              p: 0,
                              m: 0,
                              minHeight: 'unset !important',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiInputBase-root': {
                              backgroundColor: '#FFFFFF !important',
                              p: 0,
                              m: 0,
                              minHeight: 'unset !important',
                              height: '100%',
                            },
                            '& .MuiInputAdornment-root': {
                              backgroundColor: '#FFFFFF !important',
                              p: 0,
                              m: 0,
                              minHeight: 'unset !important',
                              height: '100%',
                            },
                            '&:focus': { backgroundColor: '#FFFFFF !important' },
                            '&:active': { backgroundColor: '#FFFFFF !important' },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#FFFFFF',
                              },
                            },
                          }}
                          renderValue={(selected) => {
                            return optionsUnit.find((option) => option.value === selected)?.label
                          }}
                        >
                          {optionsUnit.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  )
}

export default CombinedSelect
